import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { FiX, FiLoader, FiFileText, FiCheckCircle } from 'react-icons/fi';

import { databasesApi } from '@/modules/databases/services/api';
import { Button, Input, Select, Textarea } from '@/shared/components/ui';
import { DatabaseType, type DatabaseResponse } from '@troia-v3/types';

import { useResolveKnowledgeGap } from '../../hooks/useAgentAnalytics';
import type {
  DocumentCategoryValue,
  KnowledgeGapPattern,
} from '../../services/agentAnalyticsApi';

/**
 * Opções de categoria exibidas no select. Ordem reflete a frequência
 * esperada de uso (FAQ primeiro = default).
 */
const CATEGORY_OPTIONS: Array<{ value: DocumentCategoryValue; label: string }> =
  [
    { value: 'faq', label: 'Pergunta frequente (FAQ)' },
    { value: 'guide', label: 'Guia' },
    { value: 'policy', label: 'Política' },
    { value: 'manual', label: 'Manual' },
    { value: 'article', label: 'Artigo' },
    { value: 'tutorial', label: 'Tutorial' },
    { value: 'other', label: 'Outro' },
  ];

interface ResolveGapModalProps {
  isOpen: boolean;
  agentId: string;
  /** IDs de databases do agente (vindos do form/edit page) */
  agentDatabaseIds: string[];
  /** Gap a ser resolvido */
  gap: KnowledgeGapPattern | null;
  /**
   * Rascunho gerado pelo LLM ANTES do modal abrir (vem do GapsTab).
   * Quando ausente, modal usa fallback heurístico baseado em `gap.label`
   * e `buildSuggestedContent` (template genérico).
   */
  initialDraft?: { title: string; content: string } | null;
  onClose: () => void;
  /** Chamado após resolver com sucesso */
  onResolved: () => void;
}

type ResolveMode = 'document' | 'note';

/**
 * Modal de resolução de um gap de conhecimento. Tem dois caminhos:
 *
 * 1. **Criar documento** (default): cliente escolhe uma das databases do
 *    agente do tipo `documents`, preenche título + conteúdo pré-sugerido,
 *    backend cria o document e marca o gap como resolvido.
 *
 * 2. **Só marcar como resolvido**: cliente escreve uma nota explicando
 *    como o gap foi resolvido (fora do TroiaChat), backend só persiste
 *    a marcação sem criar document na base.
 *
 * O primeiro caminho exige que o agente tenha pelo menos 1 database do
 * tipo DOCUMENTS. Se não tiver, esconde a opção "Criar documento" e
 * mostra só o modo "Nota".
 */
export const ResolveGapModal: React.FC<ResolveGapModalProps> = ({
  isOpen,
  agentId,
  agentDatabaseIds,
  gap,
  initialDraft,
  onClose,
  onResolved,
}) => {
  const resolveMutation = useResolveKnowledgeGap(agentId);

  // Busca databases do tenant e filtra pelas que pertencem ao agente
  // E são do tipo 'documents' (únicas que aceitam o shape que o controller cria).
  const { data: databasesResponse, isLoading: loadingDatabases } = useQuery({
    queryKey: ['databases-for-agent', agentId, agentDatabaseIds],
    queryFn: () => databasesApi.getAll({ limit: 100 }),
    enabled: isOpen && agentDatabaseIds.length > 0,
    staleTime: 60 * 1000,
  });

  const documentDatabases: DatabaseResponse[] = useMemo(() => {
    if (!databasesResponse?.items) return [];
    return databasesResponse.items.filter(
      (db) =>
        db.type === DatabaseType.DOCUMENTS && agentDatabaseIds.includes(db.id),
    );
  }, [databasesResponse, agentDatabaseIds]);

  const canCreateDocument = documentDatabases.length > 0;

  const [mode, setMode] = useState<ResolveMode>('document');
  const [selectedDatabaseId, setSelectedDatabaseId] = useState<string>('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState<DocumentCategoryValue>('faq');
  const [note, setNote] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Reset state sempre que o modal abre com um gap novo. Quando o pai
  // passou um `initialDraft` (LLM já rodou no GapsTab antes de abrir o
  // modal), usa direto. Sem isso, cai no fallback heurístico baseado em
  // `gap.label` + `buildSuggestedContent` (template genérico).
  useEffect(() => {
    if (!isOpen || !gap) return;
    setMode(canCreateDocument ? 'document' : 'note');
    setSelectedDatabaseId(documentDatabases[0]?.id ?? '');
    setTitle(initialDraft?.title ?? gap.label);
    setContent(initialDraft?.content ?? buildSuggestedContent(gap));
    setCategory('faq');
    setNote('');
    setErrorMessage(null);
  }, [isOpen, gap, initialDraft, canCreateDocument, documentDatabases]);

  if (!isOpen || !gap) return null;

  const canSubmit =
    mode === 'document'
      ? selectedDatabaseId && title.trim().length > 0 && content.trim().length > 0
      : note.trim().length > 0;

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit) return;
    setErrorMessage(null);

    // Se o gap reapareceu, sinaliza pro backend substituir a resolução
    // anterior em vez de retornar a antiga (idempotente). Caso contrário,
    // o cliente clicaria em "responder novamente" e nada mudaria.
    const replaceExisting = gap.previouslyResolved;

    try {
      if (mode === 'document') {
        await resolveMutation.mutateAsync({
          gapKey: gap.gapKey,
          input: {
            databaseId: selectedDatabaseId,
            documentData: {
              title: title.trim(),
              content: content.trim(),
              category,
            },
            ...(replaceExisting ? { replaceExisting: true } : {}),
          },
        });
      } else {
        await resolveMutation.mutateAsync({
          gapKey: gap.gapKey,
          input: {
            note: note.trim(),
            ...(replaceExisting ? { replaceExisting: true } : {}),
          },
        });
      }
      onResolved();
    } catch (error) {
      setErrorMessage((error as Error).message || 'Erro ao resolver gap');
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
    >
      <div
        className="relative w-full max-w-2xl rounded-xl shadow-2xl overflow-hidden flex flex-col"
        data-testid="resolve-gap-modal"
        style={
          {
            backgroundColor: '#ffffff',
            maxHeight: '90vh',
            '--theme-text': '#1e293b',
            '--theme-muted-text': '#6b7280',
            '--theme-input-background': '#ffffff',
            '--theme-input-text': '#1e293b',
            '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
            '--theme-input-focus': '#8b5cf6',
            '--theme-card-background': '#ffffff',
            '--theme-card-text': '#334155',
            '--theme-card-border': 'rgba(0, 0, 0, 0.06)',
            '--theme-primary': '#8b5cf6',
            '--theme-border': 'rgba(0, 0, 0, 0.06)',
            '--theme-button-primary': '#8b5cf6',
            '--theme-button-primary-text': '#ffffff',
            '--theme-button-primary-hover': '#7c3aed',
            '--theme-button-secondary': '#ffffff',
            '--theme-button-secondary-text': '#334155',
            '--theme-button-secondary-hover': 'rgba(0, 0, 0, 0.10)',
            '--theme-button-ghost-hover': '#f1f5f9',
          } as React.CSSProperties
        }
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b"
          style={{ borderColor: 'rgba(0, 0, 0, 0.06)' }}
        >
          <div>
            <h2 className="text-lg font-semibold text-slate-800">
              Responder lacuna de conhecimento
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">{gap.label}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-slate-100 transition-colors"
            style={{ color: '#64748b' }}
            aria-label="Fechar"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-5 py-5 flex-1">
          {loadingDatabases && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <FiLoader className="h-4 w-4 animate-spin" />
              Carregando bases do agente...
            </div>
          )}

          {!loadingDatabases && (
            <>
              {/* Toggle de modo */}
              <div className="flex items-center gap-2 mb-5">
                {canCreateDocument && (
                  <ModeButton
                    active={mode === 'document'}
                    icon={<FiFileText className="h-4 w-4" />}
                    label="Criar documento na base"
                    onClick={() => setMode('document')}
                  />
                )}
                <ModeButton
                  active={mode === 'note'}
                  icon={<FiCheckCircle className="h-4 w-4" />}
                  label="Só marcar como resolvido"
                  onClick={() => setMode('note')}
                />
              </div>

              {!canCreateDocument && (
                <div
                  className="rounded-lg px-4 py-3 text-xs mb-4"
                  style={{
                    backgroundColor: 'rgba(245, 158, 11, 0.08)',
                    color: '#92400e',
                    border: '1px solid rgba(245, 158, 11, 0.2)',
                  }}
                >
                  Este agente não tem nenhuma base do tipo{' '}
                  <strong>Documentos</strong>. Para responder o gap criando
                  conteúdo na base, adicione uma base de documentos ao agente.
                </div>
              )}

              {mode === 'document' && canCreateDocument && (
                <div className="space-y-4">
                  {/* Select de base só aparece quando há 2+ bases vinculadas.
                      Com 1 só, auto-seleciona (já feito no useEffect do reset)
                      e esconde o controle — menos ruído visual. */}
                  {documentDatabases.length > 1 && (
                    <div>
                      <label
                        className="text-sm font-semibold text-slate-800 mb-1 block"
                      >
                        Base de documentos
                      </label>
                      <Select
                        value={selectedDatabaseId}
                        onChange={setSelectedDatabaseId}
                        options={documentDatabases.map((db) => ({ value: db.id, label: db.name }))}
                        data-testid="resolve-gap-database"
                      />
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-semibold text-slate-800 mb-1 block">
                      Título do documento
                    </label>
                    <Input
                      value={title}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setTitle(e.target.value)
                      }
                      placeholder="Ex: Política de cancelamento"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-slate-800 mb-1 block">
                      Categoria
                    </label>
                    <Select
                      value={category}
                      onChange={(v) => setCategory(v as DocumentCategoryValue)}
                      options={CATEGORY_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
                      data-testid="resolve-gap-category"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-slate-800 mb-1 block">
                      Conteúdo da resposta
                    </label>
                    <p className="text-xs text-slate-500 mb-2">
                      Escreva a resposta que o agente deve dar quando essa
                      pergunta aparecer. Pode usar markdown.
                    </p>
                    <Textarea
                      value={content}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setContent(e.target.value)
                      }
                      rows={10}
                      placeholder="Escreva a resposta aqui..."
                    />
                  </div>
                </div>
              )}

              {mode === 'note' && (
                <div>
                  <label className="text-sm font-semibold text-slate-800 mb-1 block">
                    Nota de resolução
                  </label>
                  <p className="text-xs text-slate-500 mb-2">
                    Explique como você resolveu este gap (treinou o agente
                    fora do TroiaChat, fez mudança no prompt, etc).
                  </p>
                  <Textarea
                    value={note}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setNote(e.target.value)
                    }
                    rows={6}
                    placeholder="Ex: Atualizei o prompt do agente com a política de cancelamento."
                  />
                </div>
              )}

              {errorMessage && (
                <div
                  className="rounded-md px-3 py-2 text-xs mt-4"
                  style={{
                    backgroundColor: '#fef2f2',
                    borderColor: '#fecaca',
                    border: '1px solid #fecaca',
                    color: '#991b1b',
                  }}
                >
                  {errorMessage}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer — F71: Cancelar secondary + primário shared */}
        <div
          className="flex items-center justify-end gap-3 px-5 py-4 border-t"
          style={{ borderColor: 'rgba(0, 0, 0, 0.06)' }}
        >
          <Button
            variant="secondary"
            onClick={onClose}
            data-testid="resolve-gap-cancel"
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit || resolveMutation.isPending}
            loading={resolveMutation.isPending}
            data-testid="resolve-gap-submit"
          >
            {mode === 'document' ? 'Criar documento e resolver' : 'Marcar como resolvido'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

interface ModeButtonProps {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}

const ModeButton: React.FC<ModeButtonProps> = ({ active, icon, label, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors flex-1 border"
      style={{
        backgroundColor: active ? 'rgba(139, 92, 246, 0.08)' : '#ffffff',
        borderColor: active ? '#8b5cf6' : 'rgba(0, 0, 0, 0.10)',
        color: active ? '#8b5cf6' : '#64748b',
      }}
    >
      {icon}
      {label}
    </button>
  );
};

/**
 * Gera um texto inicial sugerido para o content do document baseado no
 * gap detectado. Serve como ponto de partida — o cliente pode editar.
 */
/**
 * Fallback heurístico de conteúdo sugerido quando o LLM falhou ou
 * ainda não rodou. Texto puro, sem nenhum caractere de markdown — o
 * documento é lido pelo agente em runtime e renderizado na UI com
 * `whitespace-pre-wrap`, então quebras de linha bastam.
 */
function buildSuggestedContent(gap: KnowledgeGapPattern): string {
  const firstCustomer = gap.samples[0]?.customerMessage;
  const lines: string[] = [];
  if (firstCustomer) {
    lines.push(`Tópico: ${firstCustomer.replace(/\s+/g, ' ').trim()}`);
    lines.push('');
  }
  lines.push('[preencha aqui a resposta que o agente deve dar quando o cliente perguntar esse tópico]');
  return lines.join('\n');
}
